import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { tradeApi } from '../trade/tradeApi';
import { meApi } from '../user/meApi';
import {
  AcceptTradeOfferResponse,
  CreateTradeOfferRequest,
  ListTradeOffersRequest,
  ListTradeOffersResponse,
  MyCommittedQuantitiesResponse,
  OfferCandidatesReceiveResponse,
  OfferCandidatesRequest,
  OfferCandidatesSendResponse,
  TradeOffer,
} from 'types/interfaces/tradeOffer';

// Accepting an offer creates two trade legs and moves escrow, so the trade and
// user caches in other api instances must be refreshed explicitly (RTK tags only
// invalidate within one api instance — same pattern as blockApi).
const invalidateAcceptAffectedCaches = (dispatch: (action: unknown) => unknown) => {
  dispatch(tradeApi.util.invalidateTags(['ActiveTrades', 'InboundTradeCount', 'TradePackages']));
  dispatch(meApi.util.invalidateTags(['User']));
};

const buildCandidatesQueryString = ({
  side,
  mode,
  page,
  limit,
  search,
  sort,
}: Omit<OfferCandidatesRequest, 'userId'>): string => {
  const params = new URLSearchParams();
  params.set('side', side);
  if (mode) params.set('mode', mode);
  if (page) params.set('page', String(page));
  if (limit) params.set('limit', String(limit));
  if (search) params.set('search', search);
  if (sort) params.set('sort', sort);
  return params.toString();
};

// Canonical args for the sidebar Offers badge: the pending-incoming totalCount.
// Shared so the notifications:update socket recount can patch this exact cache
// entry (RTK cache keys are arg-sensitive).
export const PENDING_INCOMING_OFFERS_BADGE_ARGS: ListTradeOffersRequest = {
  direction: 'incoming',
  status: 'pending',
  page: 1,
  limit: 1,
};

// The Sent tab's inline count. Deliberately the same shape as the incoming args
// above so both tab counts mean the same thing — PENDING offers, not offers of
// every status. statusCounts cannot serve this: it is only ever populated for
// the direction currently being viewed, so the other tab would render no count
// at all on a bare load. limit: 1 because only totalCount is read.
export const PENDING_OUTGOING_OFFERS_BADGE_ARGS: ListTradeOffersRequest = {
  direction: 'outgoing',
  status: 'pending',
  page: 1,
  limit: 1,
};

export const tradeOffersApi = createApi({
  reducerPath: 'tradeOffersApi',
  baseQuery,
  tagTypes: ['Offers', 'Offer', 'OfferCandidates', 'CommittedQuantities'],
  endpoints: (builder) => ({
    createTradeOffer: builder.mutation<{ offer: TradeOffer }, CreateTradeOfferRequest>({
      query: (body) => ({
        url: '/trade-offers',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['Offers', 'CommittedQuantities'],
    }),

    getTradeOffer: builder.query<{ offer: TradeOffer }, string>({
      query: (offerId) => ({
        url: `/trade-offers/${offerId}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: (_result, _error, offerId) => [{ type: 'Offer', id: offerId }],
    }),

    listTradeOffers: builder.query<ListTradeOffersResponse, ListTradeOffersRequest>({
      query: ({ direction, status, page, limit }) => {
        const params = new URLSearchParams();
        params.set('direction', direction);
        if (status) params.set('status', status);
        if (page) params.set('page', String(page));
        if (limit) params.set('limit', String(limit));
        return {
          url: `/trade-offers?${params.toString()}`,
          method: 'GET',
          requiresAuth: true,
        };
      },
      providesTags: ['Offers'],
    }),

    acceptTradeOffer: builder.mutation<AcceptTradeOfferResponse, string>({
      query: (offerId) => ({
        url: `/trade-offers/${offerId}/accept`,
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: (_result, _error, offerId) => [
        'Offers',
        'CommittedQuantities',
        { type: 'Offer', id: offerId },
      ],
      async onQueryStarted(_offerId, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateAcceptAffectedCaches(dispatch);
        } catch {
          // Failed accept creates no legs; the offer tag invalidation above
          // still refreshes status (a 409 leaves the offer failed server-side).
        }
      },
    }),

    declineTradeOffer: builder.mutation<{ success: boolean }, string>({
      query: (offerId) => ({
        url: `/trade-offers/${offerId}/decline`,
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: (_result, _error, offerId) => [
        'Offers',
        'CommittedQuantities',
        { type: 'Offer', id: offerId },
      ],
    }),

    cancelTradeOffer: builder.mutation<{ success: boolean }, string>({
      query: (offerId) => ({
        url: `/trade-offers/${offerId}/cancel`,
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: (_result, _error, offerId) => [
        'Offers',
        'CommittedQuantities',
        { type: 'Offer', id: offerId },
      ],
    }),

    getMyCommittedQuantities: builder.query<MyCommittedQuantitiesResponse, void>({
      query: () => ({
        url: '/trade-offers/my-committed-quantities',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['CommittedQuantities'],
    }),

    // side=send: grouped by the target's want entries with the viewer's matching
    // have rows. side=receive: flat addable rows from the target's have list.
    // mode=full is implemented by the backend for side=receive ONLY; the send
    // panel's full mode reads the public want-list endpoint instead.
    getSendCandidates: builder.query<
      OfferCandidatesSendResponse,
      Omit<OfferCandidatesRequest, 'side'>
    >({
      query: ({ userId, ...rest }) => ({
        url: `/users/${userId}/offer-candidates?${buildCandidatesQueryString({ side: 'send', ...rest })}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['OfferCandidates'],
    }),

    getReceiveCandidates: builder.query<
      OfferCandidatesReceiveResponse,
      Omit<OfferCandidatesRequest, 'side'>
    >({
      query: ({ userId, ...rest }) => ({
        url: `/users/${userId}/offer-candidates?${buildCandidatesQueryString({ side: 'receive', ...rest })}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['OfferCandidates'],
    }),
  }),
});

export const {
  useCreateTradeOfferMutation,
  useGetTradeOfferQuery,
  useListTradeOffersQuery,
  useAcceptTradeOfferMutation,
  useDeclineTradeOfferMutation,
  useCancelTradeOfferMutation,
  useGetMyCommittedQuantitiesQuery,
  useLazyGetMyCommittedQuantitiesQuery,
  useGetSendCandidatesQuery,
  useLazyGetSendCandidatesQuery,
  useGetReceiveCandidatesQuery,
  useLazyGetReceiveCandidatesQuery,
} = tradeOffersApi;
